<template>
  <div class="space-y-6">
    <h2 class="text-2xl font-semibold">Prescription Details</h2>

    <div class="grid grid-cols-2 gap-4">
      <div>
        <p class="text-sm text-gray-600">Visit Code:</p>
        <p class="font-semibold">{{ prescriptionDetails.VisitCode }}</p>
      </div>
      <div>
        <p class="text-sm text-gray-600">Prescribed By:</p>
        <p class="font-semibold">
          {{ prescriptionDetails.prescriptions[0]?.requested_by?.Username }}
        </p>
      </div>
      <div>
        <p class="text-sm text-gray-600">Status:</p>
        <span
          class="px-2 py-1 text-xs font-semibold rounded-full"
          :class="
            getStatusStyle(
              getEffectiveStatus(prescriptionDetails.prescriptions)
            )
          "
        >
          {{ getEffectiveStatus(prescriptionDetails.prescriptions) }}
        </span>
      </div>
      <div>
        <p class="text-sm text-gray-600">Date of Prescription:</p>
        <p class="font-semibold">
          {{ formatDate(prescriptionDetails.prescriptions[0]?.created_at) }}
        </p>
      </div>
    </div>

    <table class="w-full border-collapse">
      <thead>
        <tr class="bg-gray-100">
          <th class="p-2 text-left">Generic/Brand</th>
          <th class="p-2 text-left">Dosage</th>
          <th class="p-2 text-left">Frequency</th>
          <th class="p-2 text-left">Duration</th>
          <th class="p-2 text-left">Route</th>
          <th class="p-2 text-left">Quantity</th>
          <th class="p-2 text-left">Status</th>
          <th class="p-2 text-left w-48">Notes</th>
          <th class="p-2 text-left W-10">Action</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(medication, index) in preparedPrescriptions" :key="index">
          <td class="p-2 border">
            <span v-if="editingRow === index">
              <v-select
                class="flowbite-select"
                v-model="editedMedication.inventory_item"
                :options="drugs"
                :filterable="false"
                :placeholder="Search"
                @search="onSearch"
                @change="onDrugChange"
                label="trade_name"
                :loading="isLoading"
              >
                <template #no-options="{ searching, loading }">
                  <template v-if="searching">
                    No results found for "{{ searching }}"
                  </template>
                  <template v-else-if="loading"> Loading results... </template>
                  <template v-else> Type to search for lab tests </template>
                </template>

                <template #option="option">
                  <div class="select-option">
                    <span>{{ option.trade_name }}</span>
                    <small v-if="option.description">{{
                      option.description
                    }}</small>
                  </div>
                </template>

                <template #selected-option="option">
                  <div class="selected-option">
                    {{ option.trade_name }}
                  </div>
                </template>
              </v-select>
            </span>
            <span v-else>
              {{ medication.inventory_item?.generic_name }} -
              {{ medication.inventory_item?.trade_name }}
            </span>
          </td>
          <td class="p-2 border">
            <span v-if="editingRow === index">
              <input
                :disabled="state === 'view'"
                type="number"
                v-model="editedMedication.dose"
                class="w-full border-gray-300 rounded p-1 bg-gray-50"
                required
              />
            </span>
            <span v-else>
              {{ medication.dose }}
              {{ medication.inventory_item?.drug_strength_unit?.name }}
            </span>
          </td>
          <td class="p-2 border">
            <span v-if="editingRow === index">
              <select
                :disabled="state === 'view'"
                v-model="editedMedication.inventory_dosage_frequency_id"
                @change="calculateQuantity()"
                class="w-full border-gray-300 rounded p-1 bg-gray-50"
                required
              >
                <option
                  v-for="freq in dosageFrequencies"
                  :key="freq.id"
                  :value="freq.id"
                >
                  {{ freq.name }}
                </option>
              </select>
            </span>
            <span v-else>
              {{ medication.inventory_dosage_frequency?.name }}
            </span>
          </td>
          <td class="p-2 border">
            <span v-if="editingRow === index">
              <input
                :disabled="state === 'view'"
                type="number"
                @change="calculateQuantity()"
                v-model="editedMedication.days"
                class="w-full border-gray-300 rounded p-1 bg-gray-50"
                required
              />
            </span>
            <span v-else>
              {{ medication.days }}
            </span>
          </td>
          <td class="p-2 border">
            <span v-if="editingRow === index">
              <select
                :disabled="state === 'view'"
                v-model="editedMedication.administration_route_id"
                class="w-full border-gray-300 rounded p-1 bg-gray-50"
                required
              >
                <option
                  v-for="route in adminstrationRoutes"
                  :key="route.id"
                  :value="route.id"
                >
                  {{ route.name }}
                </option>
              </select>
            </span>
            <span v-else>{{ medication.administration_route?.name }}</span>
          </td>
          <td class="p-2 border">
            <span v-if="editingRow === index">
              {{ editedMedication.quantity }}
            </span>
            <span v-else>
              {{ medication.quantity }}
            </span>
          </td>
          <td class="p-2 border">
            <span
              :class="getStatusStyle(medication.status?.name)"
              class="px-2 py-1 rounded text-xs font-semibold"
            >
              {{ medication.status?.name }}
            </span>
          </td>
          <td
            class="p-2 border max-w-32 truncate"
            data-tooltip-target="tooltip-notes"
            :data-tooltip-content="medication.notes"
          >
            {{ medication.notes }}
            <div
              id="tooltip-notes"
              role="tooltip"
              class="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-sm opacity-0 tooltip dark:bg-gray-700"
            >
              {{ medication.notes }}
              <div class="tooltip-arrow" data-popper-arrow></div>
            </div>
          </td>
          <td class="p-2 border W-10">
            <button
              v-if="editingRow === index"
              @click="saveEdit(medication.id)"
              :disabled="medication.isLoading"
              class="px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600 mr-2"
            >
              {{ medication.isLoading ? "Saving.." : "Save" }}
            </button>
            <button
              v-else-if="
                editingRow !== index && medication.status?.code === 'ST-017'
              "
              @click="startEdit(index)"
              class="px-2 py-1 bg-red-500 text-white rounded hover:bg-blue-600 mr-2"
            >
              Edit
            </button>
            <button
              v-if="
                editingRow !== index && medication.status?.code === 'ST-006'
              "
              @click="confirmAvailability(medication.id)"
              :disabled="medication.isLoading"
              class="px-2 py-1 bg-orange-400 text-white rounded hover:bg-orange-500"
            >
              {{
                medication.isLoading ? "Confirming...." : "Confirm Availability"
              }}
            </button>
            <!--
              FIX: Approve/Reject are now per-row, only offered for a drug once
              ITS OWN availability has been confirmed (ST-015). Previously these
              were two buttons at the bottom of the page that bulk-approved every
              "available OR pending" drug in one call — so approving 2 confirmed
              drugs would silently sweep up a 3rd drug you deliberately left at
              Pending, approving it without ever confirming its availability.
            -->
            <template v-if="editingRow !== index && medication.status?.code === 'ST-015'">
              <button
                @click="approveOne(medication.id)"
                :disabled="medication.approveLoading || medication.rejectLoading"
                class="px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600 mr-2"
              >
                {{ medication.approveLoading ? "Approving.." : "Approve" }}
              </button>
              <button
                @click="rejectOne(medication.id)"
                :disabled="medication.approveLoading || medication.rejectLoading"
                class="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600"
              >
                {{ medication.rejectLoading ? "Rejecting.." : "Reject" }}
              </button>
            </template>
          </td>
        </tr>
      </tbody>
    </table>

    <!--
      FIX: Bulk Approve/Reject removed — approval is now per-row above, so only
      drugs the pharmacist explicitly confirmed and approved ever reach ST-018.
      "Next" still just checks whether at least one drug has actually been
      approved, so un-approved drugs correctly stay behind on this tab.
    -->
    <div class="flex justify-end">
      <div v-if="hasAnyItemApproved(prescriptionDetails.prescriptions)">
        <button
          @click="nextTab"
          class="bg-custom-yellow text-white px-4 py-1 rounded"
        >
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/services/api";
import { initFlowbite } from "flowbite";

export default {
  name: "PrescriptionDetailsTab",
  props: {
    prescriptionDetails: {
      type: Object,
      required: true,
      isEditing: Boolean,
    },
  },
  data() {
    return {
      editingRow: null,
      editedMedication: {},
      tableHeaders: [
        "Generic",
        "Brand",
        "Dosage",
        "Frequency",
        "Medication Schedule",
        "Duration",
        "Route",
        "Refill",
        "Notes",
      ],
      confirmAvailabilityLoader: [],
      isLoading: false,
      adminstrationRoutes: [],
      dosageFrequencies: [],
      drugs: [],
      showLoader: false,
    };
  },
  mounted() {
    initFlowbite();
    this.fetchAdminstrationRoutes();
    this.fetchDosageFrequencies();
  },
  computed: {
    preparedPrescriptions() {
      return this.prescriptionDetails.prescriptions;
    },
  },
  created() {
    // Initialize per-row loading flags when the component is created
    this.prescriptionDetails.prescriptions.forEach((prescription) => {
      prescription.isLoading = false;
      prescription.approveLoading = false;
      prescription.rejectLoading = false;
    });
  },
  methods: {
    getStatusStyle(status) {
      return status === "Pending"
        ? "bg-red-100 text-red-800"
        : "bg-green-100 text-green-800";
    },
    calculateQuantity() {
      const days = parseFloat(this.editedMedication.days) || 0;
      const dose = parseFloat(this.editedMedication.dose) || 0;
      const frequency = this.dosageFrequencies.find(
        (frequency) =>
          frequency.id === this.editedMedication.inventory_dosage_frequency_id
      ).frequency;

      let selectedStrength = 1;

      if (this.editedMedication.inventory_item) {
        selectedStrength = this.editedMedication.inventory_item?.strength;
      }

      const strength = parseFloat(selectedStrength) || 1;
      let quantity = (days * dose * frequency) / strength;

      this.editedMedication.quantity =
        isNaN(quantity) || !isFinite(quantity) ? 0 : quantity;
    },
    getPriorityColor(priority) {
      switch (priority) {
        case "STAT":
          return "text-red-500";
        case "Urgent":
          return "text-orange-500";
        default:
          return "text-black";
      }
    },
    formatDate(dateString) {
      const options = {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      };
      const date = new Date(dateString);
      return date.toLocaleString("en-US", options);
    },
    getEffectiveStatus(prescriptions) {
      // Check if any prescription has a status of 'Pending'
      const hasPending = prescriptions.some(
        (prescription) => prescription.status?.name.toLowerCase() === "pending"
      );
      if (hasPending) return "Pending";
      return prescriptions[0]?.status?.name || "Unknown";
    },

    /**
     * FIX: "Next" only needs to know whether at least one drug has actually
     * reached ST-018 (Approved) — un-approved drugs (still Pending or
     * Available-but-not-yet-approved) correctly stay behind on this tab,
     * since approval is now an explicit per-row action.
     */
    hasAnyItemApproved(prescriptions) {
      if (!Array.isArray(prescriptions) || prescriptions.length === 0) {
        return false;
      }
      return prescriptions.some(
        (prescription) => prescription.status?.code === "ST-018"
      );
    },

    startEdit(index) {
      this.editingRow = index;
      this.editedMedication = {
        ...this.prescriptionDetails.prescriptions[index],
      };
    },
    async saveEdit(prescriptionId) {
      const prescription = this.prescriptionDetails.prescriptions.find(
        (p) => p.id === prescriptionId
      );
      if (prescription) {
        try {
          prescription.isLoading = true;
          const response = await apiClient.updatePharmacyPrescription(
            this.editedMedication
          );
          if (response.success) {
            prescription.isLoading = false;
            this.$emit("refetch");
            this.$toast.success("Prescription updated successfully.");
            this.editingRow = null;
            this.editedMedication = {};
          } else {
            prescription.isLoading = false;
            this.$toast.error("Failed to update prescription.");
          }
        } catch (error) {
          prescription.isLoading = false;
          this.$toast.error("Failed to update prescription.");
        }
      }
    },
    async confirmAvailability(prescriptionId) {
      const prescription = this.prescriptionDetails.prescriptions.find(
        (p) => p.id === prescriptionId
      );

      if (prescription) {
        try {
          prescription.isLoading = true;

          const response =
            await apiClient.confirmPatientPrescriptionAvailability(
              prescriptionId
            );
          if (response.success) {
            prescription.isLoading = false;
            this.$emit("refetch");
            this.$toast.success("Prescription availability confirmed.");
          } else {
            prescription.isLoading = false;
            this.$toast.error("Failed to update prescription availability.");
          }
        } catch (error) {
          prescription.isLoading = false;
          this.$toast.error("Failed to update prescription availability.");
        }
      }
    },
    onDrugChange(event) {
      console.log(event);
      let drug = this.prescriptionForm.inventory_item_id;
      this.selectDosageForm = drug.drug_dosage_form?.name;
      this.selectedStrength = drug.strength;
      this.selectedRate = 0;
      this.selectedStrengthUnit = drug.drug_strength_unit?.name;
      this.selectedDrugDosageForm = drug.drug_dosage_form?.name;
    },
    async onSearch(search, loading) {
      if (search.length < this.minChars) {
        return;
      }

      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      this.isLoading = true;
      loading(true);

      this.searchTimeout = setTimeout(async () => {
        try {
          await this.searchDrug(search);
        } catch (error) {
          this.labTests = [];
        } finally {
          this.isLoading = false;
          loading(false);
        }
      }, 300);
    },
    async searchDrug(searchString) {
      try {
        if (searchString.length < 3) {
          return;
        }

        this.isLoading = true;
        const payload = { name: searchString };
        const response = await apiClient.searchConsultationInventoryItems(
          payload
        );
        this.isLoading = false;
        if (response.success) {
          this.drugs = response.data;
        } else {
          this.$toast.error("Unable to search diagnosis.");
        }
      } catch (error) {
        this.isLoading = false;
        console.log("error", error);
        this.$toast.error(
          "Unable to search diagnosis, please check your internet connection."
        );
      }
    },
    async fetchAdminstrationRoutes() {
      try {
        const response = await apiClient.getAdminstrationRoutes();
        if (response.success) {
          this.adminstrationRoutes = response.data;
        } else {
          this.$toast.error("Unable to fetch adminstration routes.");
        }
      } catch (error) {
        console.log("error", error);
        this.$toast.error(
          "Unable to fetch adminstration routes, please check your internet connection."
        );
      }
    },
    async fetchDosageFrequencies() {
      try {
        const response = await apiClient.getDosageFrequencies();
        if (response.success) {
          this.dosageFrequencies = response.data;
        } else {
          this.$toast.error("Unable to fetch adminstration routes.");
        }
      } catch (error) {
        console.log("error", error);
        this.$toast.error(
          "Unable to fetch adminstration routes, please check your internet connection."
        );
      }
    },

    nextTab() {
      this.$emit("next");
    },

    /**
     * FIX: Shared helper for the two per-row actions below. Sends exactly ONE
     * prescription ID per call — never sweeps in sibling rows the pharmacist
     * didn't click on.
     */
    async updateSinglePrescriptionStatus(prescriptionId, status, loadingField) {
      const prescription = this.prescriptionDetails.prescriptions.find(
        (p) => p.id === prescriptionId
      );
      if (!prescription) return;

      prescription[loadingField] = true;
      try {
        const payload = { prescriptions: [prescriptionId], status };
        const response = await apiClient.updatePharmacyPrescriptionsStatus(payload);
        if (response.success) {
          this.$toast.success("Prescription status updated successfully");
          this.$emit("refetch");
        } else {
          this.$toast.error(response.message || "Unable to update prescription.");
        }
      } catch (error) {
        this.$toast.error(
          "Unable to update prescription, please check your internet connection."
        );
      } finally {
        prescription[loadingField] = false;
      }
    },

    async approveOne(prescriptionId) {
      await this.updateSinglePrescriptionStatus(prescriptionId, "ST-018", "approveLoading");
    },

    async rejectOne(prescriptionId) {
      await this.updateSinglePrescriptionStatus(prescriptionId, "ST-009", "rejectLoading");
    },
  },
};
</script>
